// src/components/AdminSortiePanel.jsx
import React, { useEffect, useState, useMemo } from "react";
import { CONFIG } from "../config/config";
import { formatHeure } from "../utils/dateHelpers";
import { useUser } from "../context/UserContext";

const IconDownload = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);
const IconTable = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 10h18M9 20V10M15 20V10" />
  </svg>
);

export default function AdminSortiePanel() {
  const token =
    sessionStorage.getItem("token") || localStorage.getItem("token");
  const { etablissement } = useUser();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [toDate, setToDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );

  const sameDay = fromDate === toDate;
  const labelFR = useMemo(
    () =>
      sameDay
        ? new Date(fromDate).toLocaleDateString("fr-FR")
        : `du ${new Date(fromDate).toLocaleDateString("fr-FR")} au ${new Date(toDate).toLocaleDateString("fr-FR")}`,
    [fromDate, toDate, sameDay],
  );

  async function updateSalle(passageId, salle) {
    await fetch(`${CONFIG.API_URL}/passages/${passageId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ salle }),
    });
    setPatients((ps) =>
      ps.map((p) => (p.id === passageId ? { ...p, salle } : p)),
    );
  }

  async function fetchPatients() {
    try {
      setLoading(true);
      const etabParam = etablissement?.etablissement_id
        ? `&etablissement_id=${encodeURIComponent(etablissement.etablissement_id)}`
        : '';
      const url = sameDay
        ? `${CONFIG.API_URL}/passages/by-date?date=${fromDate}${etabParam}`
        : `${CONFIG.API_URL}/passages/by-range?from=${fromDate}&to=${toDate}${etabParam}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPatients(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    fetchPatients();
  }, [fromDate, toDate, etablissement]); // eslint-disable-line

  const downloadBlob = async (url, filename) => {
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Téléchargement échoué");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert("Téléchargement impossible.");
    }
  };
  const dlZip = () =>
    sameDay
      ? downloadBlob(
          `${CONFIG.API_URL}/exports/day.zip?date=${fromDate}`,
          `exports_${fromDate}.zip`,
        )
      : downloadBlob(
          `${CONFIG.API_URL}/exports/range.zip?from=${fromDate}&to=${toDate}`,
          `exports_${fromDate}_to_${toDate}.zip`,
        );
  const dlXls = () =>
    sameDay
      ? downloadBlob(
          `${CONFIG.API_URL}/exports/day.xlsx?date=${fromDate}`,
          `patients_${fromDate}.xlsx`,
        )
      : downloadBlob(
          `${CONFIG.API_URL}/exports/range.xlsx?from=${fromDate}&to=${toDate}`,
          `patients_${fromDate}_to_${toDate}.xlsx`,
        );

  return (
    <section className="space-y-3">
      {/* contrôles */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex gap-2">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border px-2 py-1 rounded"
          />
          <span className="self-center">→</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border px-2 py-1 rounded"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={dlZip}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            <IconDownload className="h-4 w-4" />{" "}
            {sameDay ? "ZIP du jour" : "ZIP (plage)"}
          </button>
          <button
            onClick={dlXls}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            <IconTable className="h-4 w-4" />{" "}
            {sameDay ? "Export XLS" : "XLS (plage)"}
          </button>
        </div>
      </div>

      {/* table */}
      {loading ? (
        <p className="mt-2">Chargement…</p>
      ) : patients.length === 0 ? (
        <p className="mt-2">Aucun patient {labelFR}.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-gray-200 rounded overflow-hidden">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="py-3 px-4">Nom</th>
                <th className="px-4">Salle</th>
                <th className="px-4">Heure entrée</th>
                <th className="px-4">Heure sortie</th>
                <th className="px-4">Cotation</th>
                <th className="px-4 w-20 text-center">PDF</th>
              </tr>
            </thead>
            <tbody>
              {patients.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-gray-200 hover:bg-gray-50"
                >
                  <td className="py-3 px-4 whitespace-nowrap font-medium text-gray-900">
                    {p.prenom} {p.nom}
                  </td>
                  <td className="px-4">
                    <input
                      className="border rounded px-2 py-1 w-24"
                      defaultValue={p.salle || ""}
                      onBlur={(e) => updateSalle(p.id, e.target.value.trim())}
                    />
                  </td>
                  <td className="px-4 text-gray-600">
                    {p.createdAt
                      ? formatHeure(p.createdAt)
                      : p.created_at
                        ? formatHeure(p.created_at)
                        : "—"}
                  </td>
                  <td className="px-4 text-gray-600">
                    {p.heure_sortie
                      ? formatHeure(p.heure_sortie)
                      : p.heureSortie
                        ? formatHeure(p.heureSortie)
                        : "—"}
                  </td>
                  <td className="px-4">
                    <code className="bg-gray-100 rounded px-2 py-1">
                      {p.cotationCodes || p.cotation_codes || "—"}
                    </code>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() =>
                        downloadBlob(
                          `${CONFIG.API_URL}/exports/patient/${p.id}/pdf`,
                          `CR_${fromDate === toDate ? fromDate : `${fromDate}_${toDate}`}_${p.nom}_${p.prenom}.pdf`,
                        )
                      }
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-50"
                      title={`PDF — ${p.prenom} ${p.nom}`}
                    >
                      <IconDownload className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
